<template>
  <table class="ss-dashboard-staff-list">
    <thead>
      <tr>
        <th
          v-for="col in $_ssDSL_sortableColumns"
          :key="col.sortKey"
          :class="col.thClass"
        >
          <span class="ss-dashboard-staff-list__label">
            {{ col.label }}

            <oct-popover
              :anchorOrigin="$_ssDSL_popoverAnchorOrigin(col)"
              :transformOrigin="$_ssDSL_popoverTransformOrigin(col)"
              :closeOnPopperClick="true"
            >
              <oct-icon
                icon="filter"
                class="ss-dashboard-staff-list__filter-icon"
              />

              <template #popper>
                <oct-menu
                  class="ss-dashboard-staff-list__menu"
                  :class="{ 'ss-dashboard-staff-list__menu--popover-left': col.menuPlacement === 'left' }"
                >
                  <oct-menu-item-group title="並び替え">
                    <oct-menu-list>
                      <oct-menu-item
                        @click.native="$_ssDSL_setSort(col.sortKey, 'asc')"
                      >
                        <template #leadingIcon>
                          <oct-icon
                            v-if="$data.$_ssDSL_sortBy === col.sortKey && $data.$_ssDSL_sortOrder === 'asc'"
                            icon="done"
                          />
                          <span v-else>&nbsp;</span>
                        </template>

                        昇順で並び替え
                      </oct-menu-item>

                      <oct-menu-item
                        @click.native="$_ssDSL_setSort(col.sortKey, 'desc')"
                      >
                        <template #leadingIcon>
                          <oct-icon
                            v-if="$data.$_ssDSL_sortBy === col.sortKey && $data.$_ssDSL_sortOrder === 'desc'"
                            icon="done"
                          />
                          <span v-else>&nbsp;</span>
                        </template>

                        降順で並び替え
                      </oct-menu-item>
                    </oct-menu-list>
                  </oct-menu-item-group>

                  <oct-menu-item role="separator" />

                  <oct-menu-item
                    :disabled="$_ssDSL_isSortResetDisabled(col.sortKey)"
                    @click.native="$_ssDSL_resetStaffSort"
                  >
                    <template #leadingIcon>
                      &nbsp;
                    </template>

                    リセット
                  </oct-menu-item>
                </oct-menu>
              </template>
            </oct-popover>
          </span>
        </th>
      </tr>
    </thead>

    <tbody>
      <SSDashboardStaffListRow
        v-for="(item, index) in sortedItems"
        :key="index"
        :item="item"
        :rank="index < 3 ? index + 1 : null"
      />
    </tbody>
  </table>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue'
import OctPopover from '@/components/popover/OctPopover.vue'
import OctMenu from '@/components/menu/OctMenu.vue'
import OctMenuItem from '@/components/menu-item/OctMenuItem.vue'
import OctMenuList from '@/components/menu-list/OctMenuList.vue'
import OctMenuItemGroup from '@/components/menu-item-group/OctMenuItemGroup.vue'
import SSDashboardStaffListRow from '@/components/dashboard-staff-list-row/SSDashboardStaffListRow.vue'

export default {
  name: 'SSDashboardStaffList',
  components: {
    OctIcon,
    OctPopover,
    OctMenu,
    OctMenuItem,
    OctMenuList,
    OctMenuItemGroup,
    SSDashboardStaffListRow,
  },
  props: {
    /**
     * Row data for the dashboard staff table; sorted by `sortedItems` and passed to `SSDashboardStaffListRow` as `item`.
     * @type {Array<{
     *   name: string,
     *   role: number,
     *   performance: {
     *     sentStyleCount: number,
     *     viewedStyleCount: number,
     *     favoritedStyleCount: number,
     *     addedToCartCount: number,
     *     purchasedCount: number,
     *     purchasedCustomerCount: number,
     *     salesAmount?: number,
     *     salesAmout?: number,
     *   }
     * }>}
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data () {
    return {
      $_ssDSL_sortBy: 'sentStyleCount',
      $_ssDSL_sortOrder: 'desc',
    }
  },
  computed: {
    /**
     * Column definitions driving header labels, CSS, and `sortBy` keys for `sortedItems`.
     * @returns {Array<{ label: string, sortKey: string, thClass: (string|null), menuPlacement?: 'left'|'right' }>}
     */
    $_ssDSL_sortableColumns () {
      return [
        { label: 'スタッフ', sortKey: 'name', thClass: null },
        { label: '役割', sortKey: 'role', thClass: 'ss-dashboard-staff-list__role' },
        { label: '送信数', sortKey: 'sentStyleCount', thClass: null },
        { label: '閲覧数', sortKey: 'viewedStyleCount', thClass: null },
        { label: '♡タップ数', sortKey: 'favoritedStyleCount', thClass: null },
        { label: 'カート追加数', sortKey: 'addedToCartCount', thClass: null },
        { label: '購入数', sortKey: 'purchasedCount', thClass: null },
        { label: '購入者数', sortKey: 'purchasedCustomerCount', thClass: null, menuPlacement: 'left' },
        { label: '売上', sortKey: 'salesAmount', thClass: null, menuPlacement: 'left' },
      ]
    },
    sortedItems () {
      const sortBy = this.$data.$_ssDSL_sortBy
      const asc = this.$data.$_ssDSL_sortOrder === 'asc'
      return this.items.concat().sort((a, b) => {
        let cmp = 0
        if (sortBy === 'name') {
          cmp = String(a.name).localeCompare(String(b.name), 'ja')
        } else if (sortBy === 'role') {
          cmp = a.role - b.role
        } else if (sortBy === 'salesAmount') {
          cmp = this.$_ssDSL_salesAmountValue(a) - this.$_ssDSL_salesAmountValue(b)
        } else {
          const av = Number(a.performance && a.performance[sortBy] != null ? a.performance[sortBy] : 0)
          const bv = Number(b.performance && b.performance[sortBy] != null ? b.performance[sortBy] : 0)
          cmp = av - bv
        }
        if (cmp === 0) return 0
        return asc ? cmp : -cmp
      })
    },
  },
  methods: {
    /**
     * Popper anchor for the column sort menu (`OctPopover.anchorOrigin`).
     * @param {{ menuPlacement?: 'left'|'right' }} col
     * @returns {{ vertical: 'top', horizontal: 'left'|'right' }}
     */
    $_ssDSL_popoverAnchorOrigin (col) {
      if (col.menuPlacement === 'left') {
        return { vertical: 'top', horizontal: 'left' }
      }
      return { vertical: 'top', horizontal: 'right' }
    },
    /**
     * Popper transform origin for the column sort menu (`OctPopover.transformOrigin`).
     * @param {{ menuPlacement?: 'left'|'right' }} col
     * @returns {{ vertical: 'top', horizontal: 'left'|'right' }}
     */
    $_ssDSL_popoverTransformOrigin (col) {
      if (col.menuPlacement === 'left') {
        return { vertical: 'top', horizontal: 'right' }
      }
      return { vertical: 'top', horizontal: 'left' }
    },
    /**
     * @param {string} sortKey
     * @param {'asc'|'desc'} order
     */
    $_ssDSL_setSort (sortKey, order) {
      this.$data.$_ssDSL_sortBy = sortKey
      this.$data.$_ssDSL_sortOrder = order
    },
    /** Restore default sorting (sent styles count, descending). */
    $_ssDSL_resetStaffSort () {
      this.$data.$_ssDSL_sortBy = 'sentStyleCount'
      this.$data.$_ssDSL_sortOrder = 'desc'
    },
    /**
     * Whether the "reset sort" menu item for a column should be disabled.
     * True when that column is not the active sort key, or when the list is already on the
     * global default from {@link $_ssDSL_resetStaffSort} (sent styles count, descending),
     * so the action would be a no-op.
     * @param {string} columnSortKey - `sortKey` of the header that owns this menu.
     * @returns {boolean}
     */
    $_ssDSL_isSortResetDisabled (columnSortKey) {
      const sortBy = this.$data.$_ssDSL_sortBy
      const sortOrder = this.$data.$_ssDSL_sortOrder
      if (sortBy !== columnSortKey) return true
      return sortBy === 'sentStyleCount' && sortOrder === 'desc'
    },
    /**
     * Numeric sales amount for sorting (matches row display fallback).
     * @param {{ performance?: { salesAmount?: number, salesAmout?: number } }} row
     * @returns {number}
     */
    $_ssDSL_salesAmountValue (row) {
      const p = row.performance || {}
      const raw = p.salesAmount != null ? p.salesAmount : p.salesAmout
      return Number(raw != null ? raw : 0)
    },
  },
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.ss-dashboard-staff-list {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: center;
    height: oct-rem(48);
    padding: 0 oct-rem(16);
    vertical-align: middle;
    min-width: oct-rem(72);

    &:first-child {
      padding-left: oct-rem(32);
    }

    &:last-child {
      padding-right: oct-rem(32);
    }
  }

  .ss-dashboard-staff-list__role {
    width: min-oct-rem(120);
  }

  th {
    font-size: oct-rem(12);
    line-height: 1;
    color: $oct-theme--neutral-50;
  }
}

.ss-dashboard-staff-list__label {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: oct-rem(6);
}

.ss-dashboard-staff-list__filter-icon {
  fill: $oct-theme--neutral-50;
  width: oct-rem(16);
  height: oct-rem(16);
  cursor: pointer;
}

.ss-dashboard-staff-list__menu {
  min-width: oct-rem(240);
  margin-left: oct-rem(16);
}

.ss-dashboard-staff-list__menu--popover-left {
  margin-left: 0;
  margin-right: oct-rem(6);
}

</style>